import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { TransactionFilters } from '../components/TransactionFilters';
import { TransactionDetailsPanel } from '../components/TransactionDetailsPanel';
import { LoadingScreen } from '../components/LoadingScreen';
import { Pagination } from '../components/Pagination';
import { ApprovalAPI } from '../services/api';
import { PendingSale, UnifiedTransaction } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { AlertService } from '../services/AlertService';
import { useTransactionFilters, toBackendFilters } from '../hooks/useTransactionFilters';

export const MyFailedTransactions: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedTransaction, setSelectedTransaction] = useState<UnifiedTransaction | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);
  const [allFailedTransactions, setAllFailedTransactions] = useState<PendingSale[]>([]);
  // True totals over the full dataset (server aggregate), independent of the
  // fetched page and client-side filtering.
  const [stats, setStats] = useState<{ total: number; total_amount: number }>({ total: 0, total_amount: 0 });

  // Initialize filters - convert PendingSale to Transaction-like structure for filtering
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredTransactions,
    filterStats
  } = useTransactionFilters(allFailedTransactions.map(transaction => ({
    ...transaction,
    tx_type: 'preauth',
    nmi_response_code: transaction.preauth_response_code,
    nmi_response_text: transaction.preauth_response_text,
    status: transaction.status,
    created_by_name: transaction.created_by_name,
    // Include approval/rejection data if available
    approved_by: transaction.approved_by,
    approved_by_name: transaction.approved_by_name,
    approved_at: transaction.approved_at,
    rejected_by: transaction.rejected_by,
    rejected_by_name: transaction.rejected_by_name,
    rejected_at: transaction.rejected_at
  })));

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await ApprovalAPI.getFailedTransactionsPaged({
        offset: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        filters: toBackendFilters(filters),
      });
      setAllFailedTransactions(res.items);
      setStats({ total: res.total, total_amount: res.total_amount });
    } catch (error: any) {
      console.error('Failed to load failed transactions:', error);
      AlertService.error(error.message || 'Failed to load failed transactions');
    } finally {
      setLoading(false);
    }
  };

  // Refetch the server page on paging / filter change (filters are debounced upstream).
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filters, user?.role]);

  // Server returns the current page already; render it directly.
  const paginatedFailedTransactions = allFailedTransactions;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };



  const convertPendingSaleToTransaction = (pendingSale: PendingSale): UnifiedTransaction => {
    return {
      ...pendingSale,
      tx_type: 'preauth',
      nmi_response_code: pendingSale.preauth_response_code,
      nmi_response_text: pendingSale.preauth_response_text,
      status: 'failed',
      created_by_name: pendingSale.created_by_name,
      // Include approval/rejection data if available
      approved_by: pendingSale.approved_by,
      approved_by_name: pendingSale.approved_by_name,
      approved_at: pendingSale.approved_at,
      rejected_by: pendingSale.rejected_by,
      rejected_by_name: pendingSale.rejected_by_name,
      rejected_at: pendingSale.rejected_at
    } as unknown as UnifiedTransaction;
  };

  const handleViewDetails = (pendingSale: PendingSale) => {
    const transaction = convertPendingSaleToTransaction(pendingSale);
    setSelectedTransaction(transaction);
    setShowDetailsPanel(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransaction(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleDecryptInPanel = async (_transactionId: string, reason: string) => {
    if (!selectedTransaction) return;
    try {
      // The failed list mixes PendingSale rows (failed preauths / admin direct sales) with
      // standalone failed Transactions that have NO PendingSale and store their encrypted card
      // data on the Transaction row. Try the PendingSale endpoint first using the row's own id,
      // then fall back to the Transaction decrypt endpoint when no PendingSale exists.
      const psId = selectedTransaction.id;
      const txId = (selectedTransaction as any).transaction_id || selectedTransaction.id;

      let data;
      try {
        data = await ApprovalAPI.getPendingSaleEncryptedData(psId, true, reason);
      } catch (psErr: any) {
        try {
          data = await ApprovalAPI.getTransactionEncryptedData(txId, true, reason);
        } catch {
          throw psErr; // surface the original PendingSale error if both paths fail
        }
      }

      setDecryptedData(data);
      setShowEncryptedData(selectedTransaction.id);
    } catch (error: any) {
      AlertService.error(error.message || 'Failed to decrypt transaction data');
    }
  };

  return (
    <DashboardLayout>
      <div className="my-failed-transactions">
        <div className="page-header">
          <h2>{user?.role === 'agent' ? 'My Failed Transactions' : 'Failed Transactions'}</h2>
          <p>{user?.role === 'agent' ? 'View your transactions that failed during processing' : 'View all transactions that failed during processing'}</p>
        </div>

        <TransactionFilters
          filters={filters}
          updateFilter={updateFilter}
          clearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          transactions={filteredTransactions}
          filterStats={filterStats}
        />

        <LoadingScreen isVisible={loading} message="Loading your failed transactions..." />

        {!loading && (
          <>
            <div className="failed-transactions-summary">
              <div className="summary-card">
                <div className="summary-number">{stats.total.toLocaleString()}</div>
                <div className="summary-label">Total Failed Transactions</div>
              </div>
              <div className="summary-card">
                <div className="summary-number">
                  {formatCurrency(stats.total_amount)}
                </div>
                <div className="summary-label">Total Amount</div>
              </div>
            </div>

            {allFailedTransactions.length === 0 ? (
              <div className="no-data">
                <div className="no-data-icon">✅</div>
                <h3>No Failed Transactions</h3>
                <p>You don't have any failed transactions. Great job!</p>
              </div>
            ) : (
              <>
                <div className="failed-transactions-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>State</th>
                        <th>Category</th>
                        <th>Error Code</th>
                        <th>Error Description</th>
                        {user?.role !== 'agent' && <th>Created By</th>}
                        <th>Created</th>
                        <th>Card</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedFailedTransactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>
                            <div className="customer-info">
                              <div className="customer-name">
                                {transaction.first_name} {transaction.last_name}
                              </div>
                              <div className="customer-email">{transaction.email}</div>
                            </div>
                          </td>
                          <td className="amount">{formatCurrency(transaction.amount)}</td>
                          <td>{transaction.sale_state || 'N/A'}</td>
                          <td>{transaction.category || 'N/A'}</td>
                          <td>
                            <span className="error-code">
                              {transaction.preauth_response_code || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div className="error-description">
                              {transaction.preauth_response_text || 'N/A'}
                            </div>
                          </td>
                          {user?.role !== 'agent' && <td className="created-by">{transaction.created_by_name || transaction.created_by}</td>}
                          <td className="date">{formatDate(transaction.created_at)}</td>
                          <td>
                            <div className="card-info">
                              <span className="card-brand">{transaction.card_brand?.toUpperCase()}</span>
                              <span className="card-last4">****{transaction.last4}</span>
                              {transaction.three_ds_status === 'Y' && (
                                <span className="badge-3ds">3DS</span>
                              )}
                              {transaction.three_ds_status === 'Y-3RI' && (
                                <span className="badge-3ds">3RI</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              onClick={() => handleViewDetails(transaction)}
                              className="btn btn-outline-secondary btn-sm"
                              title="View Details"
                            >
                              📋 Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalItems={stats.total}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  pageSizeOptions={[10, 20, 30, 50, 100]}
                />
              </>
            )}
          </>
        )}

        {/* Transaction Details Panel */}
        <TransactionDetailsPanel
          transaction={selectedTransaction}
          isOpen={showDetailsPanel}
          onClose={handleCloseDetails}
          decryptedData={showEncryptedData === selectedTransaction?.id ? decryptedData : null}
          onDecryptData={handleDecryptInPanel}
        />

        <style>{`
          .my-failed-transactions {
            padding: 2rem;
          }

          .page-header {
            margin-bottom: 2rem;
          }

          .page-header h2 {
            margin: 0 0 0.5rem 0;
            color: var(--text-primary);
          }

          .page-header p {
            margin: 0;
            color: var(--text-secondary);
          }

          .failed-transactions-summary {
            display: flex;
            gap: 1rem;
            margin-bottom: 2rem;
          }

          .summary-card {
            background: var(--bg-card);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
            padding: 1.5rem;
            text-align: center;
            flex: 1;
            max-width: 200px;
          }

          .summary-number {
            font-size: 2rem;
            font-weight: bold;
            color: var(--error);
            margin-bottom: 0.5rem;
          }

          .summary-label {
            font-size: 0.875rem;
            color: var(--text-secondary);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .no-data {
            text-align: center;
            padding: 4rem 2rem;
            background: var(--bg-card);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
          }

          .no-data-icon {
            font-size: 4rem;
            margin-bottom: 1rem;
          }

          .no-data h3 {
            margin: 0 0 1rem 0;
            color: var(--text-primary);
          }

          .no-data p {
            margin: 0;
            color: var(--text-secondary);
          }

          .failed-transactions-table {
            background: var(--bg-card);
            border: 1px solid var(--border-primary);
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 2rem;
          }

          .failed-transactions-table table {
            width: 100%;
            border-collapse: collapse;
          }

          .failed-transactions-table th,
          .failed-transactions-table td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid var(--border-primary);
          }

          .failed-transactions-table th {
            background: var(--bg-secondary);
            font-weight: 600;
            color: var(--text-primary);
          }

          .failed-transactions-table tr:last-child td {
            border-bottom: none;
          }

          .order-info .order-id {
            font-weight: 600;
            color: var(--text-primary);
          }

          .order-info .order-desc {
            font-size: 0.875rem;
            color: var(--text-secondary);
            margin-top: 0.25rem;
          }

          .customer-info .customer-name {
            font-weight: 600;
            color: var(--text-primary);
          }

          .customer-info .customer-email {
            font-size: 0.875rem;
            color: var(--text-secondary);
            margin-top: 0.25rem;
          }

          .amount {
            font-weight: 600;
            color: var(--error);
          }

          .error-code {
            background: var(--bg-error);
            color: var(--error);
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-weight: 600;
            font-family: monospace;
          }

          .error-description {
            font-size: 0.875rem;
            color: var(--text-secondary);
            max-width: 200px;
          }

          .date {
            font-size: 0.875rem;
            color: var(--text-secondary);
          }

          .card-info {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .card-brand {
            font-weight: 600;
            font-size: 0.75rem;
          }

          .card-last4 {
            font-family: monospace;
            color: var(--text-secondary);
          }

          .status-badge {
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
          }

          .status-failed {
            background: rgba(220, 53, 69, 0.1);
            color: #721c24;
            border: 1px solid rgba(220, 53, 69, 0.3);
          }


          @media (max-width: 768px) {
            .my-failed-transactions {
              padding: 1rem;
            }

            .failed-transactions-summary {
              flex-direction: column;
            }

            .summary-card {
              max-width: none;
            }

            .failed-transactions-table {
              overflow-x: auto;
            }

            .failed-transactions-table table {
              min-width: 1000px;
            }

          }

          /* Details Panel Styles */
          .details-panel-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 2rem;
          }

          .details-panel {
            background: var(--bg-card);
            border-radius: 12px;
            width: 100%;
            max-width: 900px;
            max-height: 90vh;
            overflow: hidden;
            box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          }

          .details-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem 2rem;
            border-bottom: 1px solid var(--border-primary);
            background: var(--bg-secondary);
          }

          .details-header h2 {
            margin: 0;
            color: var(--text-primary);
            font-size: 1.5rem;
            font-weight: 600;
          }

          .close-btn {
            font-size: 1.5rem;
            width: 2.5rem;
            height: 2.5rem;
          }

          .details-content {
            padding: 1.5rem;
            max-height: calc(90vh - 100px);
            overflow-y: auto;
            background: var(--bg-secondary);
          }

          .details-section {
            margin-bottom: 2.5rem;
            padding: 1.5rem;
            background: var(--bg-card);
            border-radius: 8px;
            border: 1px solid var(--border-primary);
          }

          .details-section:last-child {
            margin-bottom: 0;
          }

          .details-section h3 {
            margin: 0 0 1.25rem 0;
            color: var(--text-primary);
            font-size: 1.125rem;
            font-weight: 600;
            padding-bottom: 0.75rem;
            border-bottom: 2px solid var(--primary-light);
          }

          .details-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 1.5rem 2rem;
            align-items: start;
          }

          .detail-item {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            align-items: flex-start;
          }

          .detail-item.full-width {
            grid-column: 1 / -1;
          }

          .detail-item label {
            font-weight: 500;
            color: var(--text-secondary);
            font-size: 0.875rem;
          }

          .detail-item span {
            color: var(--text-primary);
            font-size: 0.9375rem;
          }

          .monospace {
            font-family: 'Courier New', monospace;
            font-weight: 500;
          }

          .amount {
            font-weight: 600;
            color: var(--success);
            font-size: 1rem;
          }

          .status-badge {
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.875rem;
            font-weight: 500;
          }

          .status-badge.success {
            background: var(--success-light);
            color: var(--success);
          }

          .status-badge.error {
            background: var(--danger-light);
            color: var(--danger);
          }

          .status-badge.warning {
            background: var(--warning-light);
            color: var(--warning);
          }

          .tx-type-badge {
            display: inline-flex;
            align-items: center;
            padding: 0 0.5rem;
            background: var(--primary-light);
            color: var(--primary);
            border-radius: 8px;
            font-size: 0.75rem;
            font-weight: 500;
            width: fit-content;
            height: 24px;
            line-height: 1;
            vertical-align: middle;
          }

          .response-code.success {
            color: var(--success);
            font-weight: 600;
          }

          .response-code.error {
            color: var(--danger);
            font-weight: 600;
          }

          .card-brand {
            font-weight: 600;
            color: var(--primary);
          }

          .card-info {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .decrypt-warning {
            margin-top: 1rem;
            padding: 0.75rem 1rem;
            background: var(--warning-light);
            border: 1px solid var(--warning);
            border-radius: 8px;
            color: var(--warning-dark);
            font-size: 0.875rem;
            font-weight: 500;
          }

          .sale-category {
            background: var(--primary-light);
            color: var(--primary);
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.875rem;
            font-weight: 500;
          }

          .sale-state {
            background: var(--secondary-light);
            color: var(--secondary);
            padding: 0.25rem 0.5rem;
            border-radius: 4px;
            font-size: 0.875rem;
            font-weight: 500;
          }

          @media (max-width: 768px) {
            .details-panel-overlay {
              padding: 0.5rem;
            }

            .details-content {
              padding: 1rem;
              background: var(--bg-primary);
            }

            .details-header {
              padding: 1rem 1.5rem;
            }

            .details-section {
              padding: 1rem;
              margin-bottom: 1.5rem;
            }

            .details-grid {
              grid-template-columns: 1fr;
              gap: 1rem;
            }
          }

          .badge-3ds {
            display: inline-block;
            padding: 0.125rem 0.4rem;
            border-radius: 4px;
            font-size: 0.65rem;
            font-weight: 700;
            background: rgba(16, 185, 129, 0.15);
            color: #059669;
            border: 1px solid rgba(16, 185, 129, 0.3);
            margin-left: 0.25rem;
            vertical-align: middle;
          }
        `}</style>
      </div>
    </DashboardLayout>
  );
};